import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {useSelector} from 'react-redux'
import {WalletType} from 'shared/types'
import {CryptoOptions, PackageType, PaymentStatus} from './types'

type PaymentStatusModalType = {
  isOpen: boolean
  status: PaymentStatus
  error?: string
  processPackage: PackageType
}

type ConfirmPayModalType = PackageType & {isOpen: boolean}

type initialStateProps = {
  cryptoData: CryptoOptions[]
  wallet: WalletType
  confirmPayModal: ConfirmPayModalType
  packages: PackageType[]
  paymentStatusModal: PaymentStatusModalType
  isPackageModalOpen: boolean
}

const initialState: initialStateProps = {
  cryptoData: [],
  wallet: {
    credits: 0,
    freeCredits: 0,
    stickers: 0,
    photos: 0,
    videos: 0,
    virtualGifts: 0
  },
  confirmPayModal: {
    isOpen: false,
    credits: 0,
    stickers: 0,
    virtualGifts: 0,
    originPrice: 0,
    photos: 0,
    price: 0,
    id: 0,
    videos: 0,
    name: ''
  },
  packages: [],
  paymentStatusModal: {
    isOpen: false,
    status: PaymentStatus.IN_PROGRESS,
    error: '',
    processPackage: {
      credits: 0,
      stickers: 0,
      virtualGifts: 0,
      originPrice: 0,
      photos: 0,
      price: 0,
      id: 0,
      videos: 0,
      name: ''
    }
  },
  isPackageModalOpen: false
}

export const paymentModel = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setCredits: (state, {payload}: PayloadAction<WalletType>) => {
      state.wallet = payload
    },
    setPackages: (state, {payload}: PayloadAction<PackageType[]>) => {
      state.packages = payload
    },
    setConfirmPayModal: (state, {payload}: PayloadAction<Partial<ConfirmPayModalType>>) => {
      state.confirmPayModal = {...state.confirmPayModal, ...payload}
    },
    setPaymentModal: (state, {payload}: PayloadAction<Partial<PaymentStatusModalType>>) => {
      state.paymentStatusModal = {...state.paymentStatusModal, ...payload}
    },
    setPackageModalOpen: (state, {payload}: PayloadAction<boolean>) => {
      state.isPackageModalOpen = payload
    },
    setCryptoData: (state, {payload}: PayloadAction<CryptoOptions[]>) => {
      state.cryptoData = payload
    }
  }
})

export const {setPackages, setConfirmPayModal, setCredits, setPaymentModal, setPackageModalOpen, setCryptoData} = paymentModel.actions


export const useWallet = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.wallet,
    (credits) => credits
  )
)

export const usePaymentPackages = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.packages,
    (packages) => packages
  )
)

export const useConfirmPayModal = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.confirmPayModal,
    (openPackage) => openPackage
  )
)

export const usePaymentStatusModal = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.paymentStatusModal,
    (paymentModal) => paymentModal
  )
)

export const useProcessPackage = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.paymentStatusModal.processPackage,
    (processPackage) => processPackage
  )
)

export const usePaymentError = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.paymentStatusModal.error,
    (error) => error
  )
)

export const usePackageModal = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.isPackageModalOpen,
    (isOpen) => isOpen
  )
)

export const useCryptoData = () => useSelector(
  createSelector(
    (state: RootState) => state.payment.cryptoData,
    (cryptoData) => cryptoData
  )
)